import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import PharmeasyLayout from "@/components/PharmeasyLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, AlertTriangle, CheckCircle2, Clock, XCircle,
  RotateCcw, Send, Eye, Link2, MessageSquare, History, FileText,
} from "lucide-react";

const STATUS_CONFIG = {
  open: { label: "Open", color: "bg-blue-100 text-blue-700 border-blue-200", icon: AlertTriangle },
  in_progress: { label: "In Progress", color: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock },
  resolved: { label: "Resolved", color: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle2 },
  closed: { label: "Closed", color: "bg-slate-100 text-slate-600 border-slate-200", icon: XCircle },
  reopened: { label: "Reopened", color: "bg-red-100 text-red-700 border-red-200", icon: RotateCcw },
};

const StatusBadge = ({ status, size = "sm" }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.open;
  const Icon = cfg.icon;
  return (
    <Badge data-testid={`status-badge-${status}`} variant="outline" className={`${cfg.color} border gap-1 ${size === "lg" ? "text-sm px-3 py-1" : "text-xs"} font-medium`}>
      <Icon className={size === "lg" ? "h-4 w-4" : "h-3 w-3"} />
      {cfg.label}
    </Badge>
  );
};

const EscalationDetail = ({ user, onLogout }) => {
  const { escalationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [esc, setEsc] = useState(null);

  // Status update
  const [newStatus, setNewStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Comment
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchDetail = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/pharmeasy-escalations/${escalationId}`);
      setEsc(data);
    } catch (err) {
      toast.error("Escalation not found");
      navigate("/pharmeasy/escalations");
    } finally {
      setLoading(false);
    }
  }, [escalationId, navigate]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleStatusUpdate = async () => {
    if (!newStatus) return;
    setUpdatingStatus(true);
    try {
      await axios.put(`${API}/pharmeasy-escalations/${escalationId}/status`, {
        status: newStatus,
        note: statusNote,
      });
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      setNewStatus("");
      setStatusNote("");
      fetchDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/pharmeasy-escalations/${escalationId}/comment`, { text: comment });
      toast.success("Comment added");
      setComment("");
      fetchDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PharmeasyLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
        </div>
      </PharmeasyLayout>
    );
  }

  if (!esc) return null;

  const allStatuses = ["open", "in_progress", "resolved", "closed", "reopened"];
  const availableStatuses = allStatuses.filter((s) => s !== esc.status);

  const infoFields = [
    ["Source ID", esc.source_id],
    ["Order ID", esc.order_id],
    ["LBN", esc.lbn],
    ["Escalation Team", esc.escalation_team],
    ["Escalation Type", esc.escalation_type],
    ["Supplier", esc.supplier],
    ["City", esc.city],
    ["EDG", esc.edg],
    ["Opening Date", esc.opening_date],
    ["Content Type", esc.content_type],
    ["Disposition Intent", esc.disposition_intent],
    ["Disposition Action", esc.disposition_action],
    ["File", esc.file_name],
    ["Upload Batch", esc.file_upload_id],
    ["Created By", esc.created_by],
    ["Created At", esc.created_at ? new Date(esc.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "-"],
    ["Updated At", esc.updated_at ? new Date(esc.updated_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "-"],
  ];

  return (
    <PharmeasyLayout user={user} onLogout={onLogout}>
      <div data-testid="escalation-detail-page" className="space-y-6 p-4 md:p-6 max-w-[1200px] mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            data-testid="back-btn"
            variant="outline"
            size="sm"
            onClick={() => navigate("/pharmeasy/escalations")}
            className="gap-1"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 data-testid="escalation-id-heading" className="text-lg font-bold text-slate-800">{esc.id}</h1>
              <StatusBadge status={esc.status} size="lg" />
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              {esc.order_id && `Order: ${esc.order_id}`}
              {esc.order_id && esc.lbn && " | "}
              {esc.lbn && `LBN: ${esc.lbn}`}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Info + Linked Shipment */}
          <div className="lg:col-span-2 space-y-6">
            {/* Escalation Info */}
            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-teal-600" /> Escalation Details
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5">
                  {infoFields.map(([label, value]) => (
                    <div key={label} className="flex justify-between py-1.5 border-b border-slate-50 text-sm">
                      <span className="text-slate-500 text-xs">{label}</span>
                      <span className="text-slate-800 font-medium text-sm text-right max-w-[60%] truncate">{value || "-"}</span>
                    </div>
                  ))}
                </div>
                {esc.remarks && (
                  <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm">
                    <span className="text-xs font-semibold text-slate-500">Remarks: </span>
                    <span className="text-slate-700 text-sm">{esc.remarks}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Linked Shipment */}
            {esc.linked_shipment && (
              <Card>
                <CardHeader className="pb-2 pt-4 px-4">
                  <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Link2 className="h-4 w-4 text-teal-600" /> Linked Shipment
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {Object.entries(esc.linked_shipment).map(([k, v]) => (
                      <div key={k} className="text-xs">
                        <span className="text-slate-400 block">{k}</span>
                        <span className="font-medium text-slate-700 text-sm">{v || "-"}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Status History */}
            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <History className="h-4 w-4 text-teal-600" /> Status History ({esc.status_history?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-3">
                  {(esc.status_history || []).slice().reverse().map((h, i) => (
                    <div key={i} className="flex items-start gap-3 text-xs">
                      <div className="flex flex-col items-center mt-0.5">
                        <div className="w-2 h-2 rounded-full bg-teal-400 shrink-0" />
                        {i < (esc.status_history?.length || 0) - 1 && <div className="w-px h-6 bg-slate-200 mt-1" />}
                      </div>
                      <div className="flex-1 pb-1">
                        <div className="flex items-center gap-2">
                          {h.from ? (
                            <>
                              <StatusBadge status={h.from} />
                              <span className="text-slate-400">&rarr;</span>
                            </>
                          ) : (
                            <span className="text-slate-400 text-xs">Created as</span>
                          )}
                          <StatusBadge status={h.to} />
                        </div>
                        <div className="text-slate-500 mt-1">
                          by <span className="font-medium text-slate-700">{h.by}</span>
                          <span className="text-slate-300 mx-1.5">|</span>
                          {new Date(h.at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
                        </div>
                        {h.note && <div className="text-slate-500 mt-0.5 italic">{h.note}</div>}
                      </div>
                    </div>
                  ))}
                  {(!esc.status_history || esc.status_history.length === 0) && (
                    <div className="text-xs text-slate-400 italic py-2">No history available</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Actions + Comments */}
          <div className="space-y-6">
            {/* Status Update */}
            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold text-slate-700">Update Status</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-2">
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger data-testid="status-select" className="h-9 text-sm">
                    <SelectValue placeholder="Select new status..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableStatuses.map((s) => (
                      <SelectItem key={s} value={s}>{STATUS_CONFIG[s]?.label || s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  data-testid="status-note-input"
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  placeholder="Add a note (optional)"
                  className="h-9 text-sm"
                />
                <Button
                  data-testid="update-status-btn"
                  onClick={handleStatusUpdate}
                  disabled={!newStatus || updatingStatus}
                  className="w-full bg-teal-600 hover:bg-teal-700 h-9 text-sm"
                >
                  {updatingStatus ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                  Update Status
                </Button>
              </CardContent>
            </Card>

            {/* Comments */}
            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-teal-600" /> Comments ({esc.comments?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-2 max-h-[300px] overflow-y-auto mb-3">
                  {(esc.comments || []).slice().reverse().map((c, i) => (
                    <div key={i} className="text-xs bg-slate-50 rounded-lg p-3">
                      <div className="flex justify-between text-slate-400 mb-1">
                        <span className="font-medium text-slate-600">{c.by}</span>
                        <span className="text-xs">{new Date(c.at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</span>
                      </div>
                      <div className="text-slate-700 text-sm">{c.text}</div>
                    </div>
                  ))}
                  {(!esc.comments || esc.comments.length === 0) && (
                    <div className="text-xs text-slate-400 italic py-4 text-center">No comments yet</div>
                  )}
                </div>
                <div className="space-y-2">
                  <Textarea
                    data-testid="comment-input"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="text-sm min-h-[60px]"
                    rows={3}
                  />
                  <Button
                    data-testid="add-comment-btn"
                    onClick={handleAddComment}
                    disabled={!comment.trim() || submitting}
                    className="w-full bg-teal-600 hover:bg-teal-700 h-9 text-sm gap-1"
                  >
                    {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    Add Comment
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PharmeasyLayout>
  );
};

export default EscalationDetail;
